<template>
    <Markets
        v-bind:loading="!loaded"
        v-bind:isLive="match.isLive"
        v-bind:isShowExpandList="isShowExpandList"
        class="notranslate"
    >
        <template #mainMarket>
            <div
                v-for="(row, index) in oddsFormatted"
                v-bind:key="index"
                class="mainMarketContent"
                v-bind:class="{ _live: match.isLive }"
            >
                <div class="row marketTitleContainer">
                    <div
                        v-for="title in row.titles"
                        v-bind:key="title"
                        class="col marketTitle"
                    >
                        {{ title }}
                    </div>
                </div>
                <div class="row oddBoxContainer">
                    <div
                        v-for="(groupedOddsList, i) in row.data"
                        v-bind:key="i"
                        class="col oddArea"
                    >
                        <div
                            v-for="(oddsList, oddsListIndex) in groupedOddsList.oddsList"
                            v-bind:key="oddsListIndex"
                            class="row"
                        >
                            <div class="oddTypeGroup">
                                <template v-if="oddsList[0]">
                                    <div
                                        v-for="priceOption in oddsList[0].priceOption"
                                        v-bind:key="priceOption.option"
                                        class="oddType"
                                    >
                                        {{ getOddsLabel(oddsList[0], priceOption) }}
                                    </div>
                                </template>
                            </div>
                            <template v-for="(oddsInfo, oddsInfoIndex) in oddsList">
                                <div
                                    v-if="oddsInfo === null"
                                    v-bind:key="'null' + oddsInfoIndex"
                                    class="col oddBoxGroup"
                                />
                                <div
                                    v-else
                                    v-bind:key="oddsInfoIndex"
                                    class="col oddBoxGroup"
                                >
                                    <Odds
                                        v-for="priceOption in oddsInfo.priceOption"
                                        v-bind:key="priceOption.option"
                                        v-bind:oddsInfo="oddsInfo"
                                        v-bind:priceOption="priceOption"
                                        v-bind:matchInfo="match"
                                        v-bind:displaySection="displaySection"
                                    />
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template v-if="moreMarketOddsGroup.length || markets.length || hasPreBuilt" #moreMarket="{ isCollapse }">
            <MoreMarketBetBuilderCombination
                v-if="hasPreBuilt"
                v-bind:matchId="match.eventId"
                v-bind:isLive="match.isLive"
                v-bind:isCollapse="isCollapse"
                v-bind:recommendation="recommendation"
                v-bind:event="preBuiltEvent"
                v-bind:selections="preBuiltSelections"
            />
            <template
                v-for="(moreMarketOddsList, index) in moreMarketOddsGroup"
            >
                <FastMarket
                    v-if="isFastMarket(moreMarketOddsList[0].marketGroup.id)"
                    v-bind:key="`fast:${index}:${moreMarketOddsList[0].oddsId}:${moreMarketOddsList[0].marketType}`"
                    v-bind:oddsList="moreMarketOddsList"
                    v-bind:match="match"
                    v-bind:class="{ __live: match.isLive }"
                    v-bind:marketOrder="index + 1"
                    v-bind:isCollapse="isCollapse"
                    v-bind:displaySection="displaySection"
                />
                <MoreMarket
                    v-else
                    v-bind:key="`more:${index}:${moreMarketOddsList[0].oddsId}:${moreMarketOddsList[0].marketType}`"
                    v-bind:oddsList="moreMarketOddsList"
                    v-bind:match="match"
                    v-bind:class="{ __live: match.isLive }"
                    v-bind:marketOrder="index + 1"
                    v-bind:isCollapse="isCollapse"
                    v-bind:displaySection="displaySection"
                />
            </template>
            <template v-for="market in markets" v-bind:key="market.id">
                <Market
                    v-if="market.selections.length"
                    v-bind:class="{ __live: match.isLive }"
                    v-bind:isCollapse="isCollapse"
                    v-bind:market="market"
                    v-bind:match="match"
                />
            </template>
        </template>
    </Markets>
</template>

<script lang="ts">
    import flatMap from 'lodash/flatMap';
    import uniq from 'lodash/uniq';
    import { storeToRefs } from 'pinia';
    import type { PropType } from 'vue';
    import { computed, defineComponent, toRefs, watch } from 'vue';
    import FastMarket from '@/components/match/FastMarket.vue';
    import Market from '@/components/match/Market.vue';
    import Markets from '@/components/match/Markets.vue';
    import MoreMarket from '@/components/match/MoreMarket.vue';
    import Odds from '@/components/match/Odds.vue';
    import MoreMarketBetBuilderCombination from '@/components/recommendations/MoreMarketBetBuilderCombination.vue';
    import type { IBetBuilderRecommendation, IRecommendationEvent } from '@/interface/IBetBuilderRecommendation';
    import type { IMarket, IMatch } from '@/interface/IMatch';
    import type { IOdds, IPriceOption, MainMarketOdds } from '@/interface/IOdds';
    import { DisplaySection } from '@/interface/enum';
    import { SboMarketPointOrderContext } from '@/libraries/composables/SboMarketPointOrderContext';
    import { groupMoreMarketOddsForDisplay } from '@/libraries/composables/useMoreMarket';
    import { getBetOptionNameForMainMarket, getMainMarketTitle } from '@/libraries/helpers/betOptionDisplayHelper';
    import { i18n } from '@/libraries/i18n';
    import { getMainMarketConfig } from '@/libraries/marketTypeDisplayRule';
    import { useOdds } from '@/libraries/oddsApi/composables/useOdds';
    import {
        getMainMarketOddsListBySportType,
        isFastMarket,
        isMainMarket,
    } from '@/libraries/oddsDisplay';
    import type { IPreBuiltSelectionWithMarket } from '@/libraries/recommendation/useMatchPreBuiltRecommendation';
    import { useRootStore } from '@/stores/rootStore';
    import { EnumPresetFilterType } from '@/types';

    export default defineComponent({
        name: 'SboMarkets',
        components: {
            Odds,
            Markets,
            MoreMarket,
            FastMarket,
            Market,
            MoreMarketBetBuilderCombination,
        },
        props: {
            match: {
                type: Object as PropType<IMatch>,
                required: true,
            },
            isOpenMoreMarket: {
                type: Boolean,
                default: false,
            },
            isAllMoreMarketExpanded: {
                type: Boolean,
                default: false,
            },
            markets: {
                type: Array as PropType<IMarket[]>,
                default: () => [],
            },
            displaySection: {
                type: Number as PropType<DisplaySection>,
                default: DisplaySection.Unknown,
            },
            hasPreBuilt: {
                type: Boolean,
                default: false,
            },
            recommendation: {
                type: Object as PropType<IBetBuilderRecommendation | null>,
                default: null,
            },
            preBuiltEvent: {
                type: Object as PropType<IRecommendationEvent | null>,
                default: null,
            },
            preBuiltSelections: {
                type: Array as PropType<IPreBuiltSelectionWithMarket[]>,
                default: () => [],
            },
        },
        setup(props) {
            const { match, isOpenMoreMarket, markets, hasPreBuilt } = toRefs(props);

            const { isMixParlayMode } = storeToRefs(useRootStore());

            const isLive = computed(() => match.value.isLive);
            const presetFilter = computed(() => (isLive.value ? EnumPresetFilterType.Live : EnumPresetFilterType.NonLive));
            const betTypeGroupIds = computed(() => {
                if (isOpenMoreMarket.value) return null;
                return uniq(flatMap(getMainMarketConfig(match.value.sportType, isLive.value)).flatMap(x => x.betTypeGroups));
            });
            const { record } = SboMarketPointOrderContext.inject();
            const { oddsList, loaded } = useOdds({
                eventId: match.value.eventId,
                betTypeGroupIds,
                isMixParlayMode,
                presetFilter,
                isLive,
                isEnabled: true,
                sportType: match.value.sportType,
                leagueSize: match.value.leagueSize,
            });

            watch(oddsList, record, { immediate: true });

            const oddsFormatted = computed(() => {
                const odds = getMainMarketOddsListBySportType(oddsList.value ?? [], match.value.sportType, match.value.isLive);

                return odds.map((row, index) => {
                    const isFirstRow = index === 0;
                    const hasRow = row.some(x => x.rawOddsList.length > 0);
                    const isSecondRowHasOdds = index === 1 && hasRow;
                    const isThirdRowHasOdds = index === 2 && hasRow;
                    const isShow = isFirstRow || isSecondRowHasOdds || (isOpenMoreMarket.value && isThirdRowHasOdds);

                    return ({
                        data: row.map<MainMarketOdds>((x) => {
                            const oddsListBeforeExpand = x.oddsList.length > 0 ? [x.oddsList[0]] : [];
                            return ({
                                oddsList: isOpenMoreMarket.value ? x.oddsList : oddsListBeforeExpand,
                                rawOddsList: x.rawOddsList,
                                betTypeGroups: x.betTypeGroups,
                                marketType: x.marketType,
                            });
                        }),
                        titles: row.map(x => getMainMarketTitle(match.value, x.betTypeGroups[0], x.marketType)),
                        isShow,
                    });
                }).filter(x => x.isShow);
            });

            function getOddsLabel(odds: IOdds, priceOption: IPriceOption) {
                return getBetOptionNameForMainMarket(match.value, odds, priceOption);
            }

            const moreMarketOddsGroup = computed(() => {
                const moreMarketOddsList = (oddsList.value ?? [])
                    .filter(odd => !isMainMarket(match.value.sportType, match.value.isLive, odd.marketGroup.id, odd.marketType));
                return groupMoreMarketOddsForDisplay(moreMarketOddsList);
            });

            const isShowExpandList = computed(() => props.isOpenMoreMarket && (
                !!moreMarketOddsGroup.value.length
                || !!markets.value.length
                || hasPreBuilt.value
            ));

            return {
                oddsFormatted,
                moreMarketOddsGroup,
                isFastMarket,
                isShowExpandList,
                i18n,
                loaded,
                getOddsLabel,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @import "@/scss/components/match";
    @import "@/scss/components/odd";

    .oddBoxContainer {
        min-height: 10rem;
    }

    .marketTitle {
        word-break: break-word;
    }

</style>
